// src/Container/Login/login.js

import React, { useState, useRef, useEffect } from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';

import AlertBox from '../../Components/Alert/alert';
import ConfirmDialog from '../../Components/ConfirmDialog/ConfirmDialog';
import { getFriendlyError } from '../../utils/firebaseErrors';

import {
  signInWithEmailAndPassword,
  signInWithPopup,
  applyActionCode,
  setPersistence,
  browserSessionPersistence,
  getAdditionalUserInfo,
  deleteUser,
} from 'firebase/auth';

import { collection, query, where, getDocs, doc, setDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, provider, db, functions } from '../../Firebase/firebase';

import GooogleImage from '../../assets/images/Google-G.png';

import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import CloseIcon from '@mui/icons-material/Close';

import { useSearchParams } from 'react-router-dom';

const theme = createTheme();

export default function Login({ open, onClose, onSwitchToSignup }) {
  const [userInfo, setUserInfo] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loadingAction, setLoadingAction] = useState(null);
  const [alertState, setAlertState] = useState({
    visible: false,
    severity: '',
    message: '',
  });
  const [resetDialogOpen, setResetDialogOpen] = useState(false);

  const [rogueAccountDialog, setRogueAccountDialog] = useState(false);
  const [resetMode, setResetMode] = useState(false);
  const [resetOtp, setResetOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [verificationSuccess, setVerificationSuccess] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const timerRef = useRef(null);

  const oobCode = searchParams.get('oobCode');

  useEffect(() => {
    return () => clearTimeout(timerRef.current);
  }, []);

  useEffect(() => {
    if (!open) {
      setUserInfo({ email: '', password: '' });
      setResetMode(false);
      setResetOtp('');
      setNewPassword('');
    }
  }, [open]);

  const showTemporaryAlert = (severity, message) => {
    setAlertState({ visible: true, severity, message });
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setAlertState({ visible: false, severity: '', message: '' });
    }, 3000);
  };

  useEffect(() => {
    const verifyEmailCode = async () => {
      if (open && oobCode) {
        setLoadingAction('VERIFYING');
        try {
          await applyActionCode(auth, oobCode);
          setVerificationSuccess(true);

          setTimeout(() => {
            onClose();
          }, 3000);

          searchParams.delete('oobCode');
          setSearchParams(searchParams);
        } catch (error) {
          showTemporaryAlert('error', getFriendlyError(error));
          searchParams.delete('oobCode');
          setSearchParams(searchParams);
        } finally {
          setLoadingAction(null);
        }
      }
    };

    verifyEmailCode();
  }, [open, oobCode, searchParams, setSearchParams, onClose]);

  const checkMailingListAccess = async (email) => {
    try {
      const q = query(collection(db, 'mailingList'), where('email', '==', email));
      const querySnapshot = await Promise.race([
        getDocs(q),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('firestore-timeout')), 8000),
        ),
      ]);
      return !querySnapshot.empty;
    } catch (error) {
      console.error('Error checking mailing list:', error);
      return false;
    }
  };

  const checkUnsubscribeStatus = async (email) => {
    try {
      const q = query(collection(db, 'unsubscribeRequests'), where('email', '==', email));
      const querySnapshot = await Promise.race([
        getDocs(q),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('firestore-timeout')), 8000),
        ),
      ]);
      return !querySnapshot.empty;
    } catch (error) {
      console.error('Error checking unsubscribe status:', error);
      return false;
    }
  };

  const handleSubmit = async (event) => {
    if (event) event.preventDefault();
    setLoadingAction('EMAIL');

    try {
      await setPersistence(auth, browserSessionPersistence);

      const userCredential = await signInWithEmailAndPassword(
        auth,
        userInfo.email,
        userInfo.password,
      );
      const user = userCredential.user;

      if (!user.emailVerified) {
        setLoadingAction(null);
        await auth.signOut();
        setRogueAccountDialog(true);
        return;
      }

      const hasAccess = await checkMailingListAccess(user.email);

      if (!hasAccess) {
        const isUnsubscribed = await checkUnsubscribeStatus(user.email);

        if (!isUnsubscribed) {
          try {
            const mailingRef = doc(db, 'mailingList', user.uid);
            await setDoc(
              mailingRef,
              {
                userName: user.displayName || '',
                companyName: '',
                email: user.email.toLowerCase().trim(),
                addedAt: new Date().toISOString(),
              },
              { merge: true },
            );
          } catch (healError) {
            console.error('Failed to auto-heal mailingList document:', healError);
            setLoadingAction(null);
            await auth.signOut();
            showTemporaryAlert('error', 'Login error. Please try again.');
            return;
          }
        } else {
          console.log(
            `User ${user.email} is in unsubscribeRequests. Skipping auto-heal.`,
          );
        }
      }
      onClose();
    } catch (error) {
      if (
        error.code === 'auth/invalid-credential' ||
        error.code === 'auth/wrong-password'
      ) {
        try {
          const checkAccount = httpsCallable(functions, 'checkAccountStatus');
          const statusResult = await checkAccount({
            email: userInfo.email.toLowerCase().trim(),
          });

          const { exists, providers } = statusResult.data;

          if (!exists) {
            showTemporaryAlert('warning', 'You do not have an account. Sign Up.');
            setLoadingAction(null);
            return;
          }

          if (
            exists &&
            providers.includes('google.com') &&
            !providers.includes('password')
          ) {
            showTemporaryAlert('warning', 'Please Sign in with Google');
            setLoadingAction(null);
            return;
          }

          showTemporaryAlert('error', 'Invalid login credentials');
          setLoadingAction(null);
          return;
        } catch (backendError) {
          console.error('Error checking account status:', backendError);
          showTemporaryAlert('error', 'Account not found or invalid credentials.');
          setLoadingAction(null);
          return;
        }
      }

      showTemporaryAlert('error', getFriendlyError(error));
      setLoadingAction(null);
    }
  };

  const handleClearRogueAccount = async () => {
    setRogueAccountDialog(false);
    setLoadingAction('EMAIL');

    try {
      const deleteUnverified = httpsCallable(functions, 'deleteUnverifiedAccount');
      await deleteUnverified({ email: userInfo.email.toLowerCase().trim() });

      setLoadingAction(null);
      showTemporaryAlert(
        'success',
        'Incomplete account cleared. Routing you to Sign Up...',
      );

      setTimeout(() => {
        onSwitchToSignup();
      }, 2000);
    } catch (error) {
      setLoadingAction(null);
      console.error('[LOGIN FLOW] Error clearing rogue account:', error);

      const displayMessage =
        error.message && error.message !== 'INTERNAL'
          ? error.message
          : 'Account could not be cleared. Please try again later.';

      showTemporaryAlert('error', displayMessage);
    }
  };

  const handleGoogleButton = async () => {
    setLoadingAction('GOOGLE');
    try {
      await setPersistence(auth, browserSessionPersistence);

      const result = await signInWithPopup(auth, provider);
      if (!result || !result.user) throw new Error('No user data returned.');

      const user = result.user;
      const email = user.email.toLowerCase().trim();

      const additionalInfo = getAdditionalUserInfo(result);
      if (additionalInfo && additionalInfo.isNewUser) {
        await deleteUser(user);
        setLoadingAction(null);
        showTemporaryAlert('warning', 'You do not have an account. Sign Up.');
        return;
      }

      const hasAccess = await checkMailingListAccess(email);

      if (!hasAccess) {
        const isUnsubscribed = await checkUnsubscribeStatus(email);

        if (!isUnsubscribed) {
          try {
            const mailingRef = doc(db, 'mailingList', user.uid);
            await setDoc(
              mailingRef,
              {
                userName: user.displayName || '',
                companyName: '',
                email: user.email.toLowerCase().trim(),
                addedAt: new Date().toISOString(),
              },
              { merge: true },
            );
          } catch (healError) {
            console.error('Failed to auto-heal mailingList document:', healError);
            setLoadingAction(null);
            await auth.signOut();
            showTemporaryAlert('error', 'Login error. Please try again.');
            return;
          }
        } else {
          console.log(`User ${email} is in unsubscribeRequests. Skipping auto-heal.`);
        }
      }

      onClose();
    } catch (error) {
      if (error.code === 'auth/popup-closed-by-user') {
        setLoadingAction(null);
        return;
      }
      showTemporaryAlert('error', 'Google Sign In failed.');
      setLoadingAction(null);
    }
  };

  const handlePassword = async () => {
    if (!userInfo.email) {
      showTemporaryAlert(
        'warning',
        'Please type your email address in the box first to reset your password.',
      );
      return;
    }

    setLoadingAction('RESET');

    try {
      const sendResetOtp = httpsCallable(functions, 'sendPasswordResetOtp');
      await sendResetOtp({ email: userInfo.email });
      setLoadingAction(null);
      setResetMode(true);
      showTemporaryAlert('success', 'Reset code sent to your email.');
    } catch (error) {
      setLoadingAction(null);
      const displayMessage =
        error.message && error.message !== 'INTERNAL'
          ? error.message
          : getFriendlyError(error);
      showTemporaryAlert('error', displayMessage);
    }
  };

  const submitNewPassword = async (event) => {
    if (event) event.preventDefault();

    if (!resetOtp || resetOtp.length !== 6) {
      showTemporaryAlert('warning', 'Please enter the 6-digit code');
      return;
    }
    if (!newPassword || newPassword.length < 6) {
      showTemporaryAlert('warning', 'Password must be at least 6 characters');
      return;
    }

    setLoadingAction('VERIFYING_RESET');

    try {
      const verifyReset = httpsCallable(functions, 'verifyPasswordResetOtp');
      await verifyReset({
        email: userInfo.email,
        otp: resetOtp,
        newPassword: newPassword,
      });

      setResetMode(false);
      setResetOtp('');
      setNewPassword('');
      setLoadingAction(null);
      setResetDialogOpen(true);
    } catch (error) {
      setLoadingAction(null);
      const displayMessage =
        error.message && error.message !== 'INTERNAL'
          ? error.message
          : getFriendlyError(error);
      showTemporaryAlert('error', displayMessage);
    }
  };

  const inputStyle = {
    '& .MuiInputBase-root': { height: 40 },
    '& .MuiInputLabel-root': { transform: 'translate(14px, 10px) scale(1)' },
    '& .MuiInputLabel-shrink': { transform: 'translate(14px, -6px) scale(0.75)' },
  };

  return (
    <ThemeProvider theme={theme}>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth='xs'
        fullWidth
        sx={{ zIndex: 100000 }}
        PaperProps={{
          sx: { borderRadius: 3, overflow: 'visible', position: 'relative' },
        }}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.2)' } }}>
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', top: 8, right: 8, zIndex: 100 }}>
          <CloseIcon />
        </IconButton>

        <DialogContent
          sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <ConfirmDialog
            open={rogueAccountDialog}
            onClose={() => setRogueAccountDialog(false)}
            onConfirm={handleClearRogueAccount}
            message='There was an error in your Sign Up. Click the Tick to clear this incomplete account and Sign Up again.'
            severity='warning'
            sx={{ zIndex: 100005 }}
          />

          <ConfirmDialog
            open={resetDialogOpen}
            onClose={() => setResetDialogOpen(false)}
            onConfirm={() => setResetDialogOpen(false)}
            message='Password successfully reset! You can now sign in with your new password.'
            severity='success'
            sx={{ zIndex: 100005 }}
          />

          <div
            style={{
              position: 'absolute',
              top: -60,
              left: 0,
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              zIndex: 100005,
            }}>
            <AlertBox
              visible={alertState.visible}
              severity={alertState.severity}
              message={alertState.message}
            />
          </div>

          <Box
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}>
            {verificationSuccess ? (
              <Box sx={{ mt: 1, width: '100%', textAlign: 'center' }}>
                <Typography
                  component='h2'
                  variant='h5'
                  sx={{ color: '#2e7d32', mb: 2, fontWeight: 'bold' }}>
                  Email Verified!
                </Typography>
                <Typography variant='body1' sx={{ mb: 3 }}>
                  Your account is fully verified. We are logging you in...
                </Typography>
              </Box>
            ) : oobCode || loadingAction === 'VERIFYING' ? (
              <Box
                sx={{
                  py: 3,
                  width: '100%',
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}>
                <CircularProgress size={40} sx={{ color: '#1976d2', mb: 3 }} />
                <Typography variant='body1' sx={{ fontWeight: 500, color: '#333' }}>
                  Securely verifying your email...
                </Typography>
              </Box>
            ) : resetMode ? (
              <>
                <Typography component='h1' variant='h5' sx={{ mb: 1 }}>
                  Reset Password
                </Typography>
                <Typography
                  variant='body2'
                  sx={{ mb: 2, textAlign: 'center', color: 'text.secondary' }}>
                  Enter the 6-digit code sent to <strong>{userInfo.email}</strong> to set
                  a new password.
                </Typography>

                <Box
                  component='form'
                  onSubmit={submitNewPassword}
                  noValidate
                  sx={{ mt: 1, width: '100%' }}>
                  <TextField
                    size='small'
                    margin='dense'
                    required
                    fullWidth
                    label='6-Digit Code'
                    disabled={loadingAction !== null}
                    value={resetOtp}
                    autoComplete='one-time-code'
                    onChange={(e) =>
                      setResetOtp(e.target.value.replace(/\D/g, '').slice(0, 6))
                    }
                    sx={inputStyle}
                    inputProps={{
                      maxLength: 6,
                      style: {
                        textAlign: 'center',
                        letterSpacing: '0.2em',
                        fontWeight: 'bold',
                      },
                    }}
                  />

                  <TextField
                    size='small'
                    margin='dense'
                    required
                    fullWidth
                    label='New Password'
                    type={showPassword ? 'text' : 'password'}
                    disabled={loadingAction !== null}
                    value={newPassword}
                    autoComplete='new-password'
                    onChange={(e) => setNewPassword(e.target.value)}
                    sx={inputStyle}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position='end'>
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            size='small'>
                            {showPassword ? (
                              <VisibilityOff fontSize='small' />
                            ) : (
                              <Visibility fontSize='small' />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Button
                    type='submit'
                    fullWidth
                    variant='contained'
                    disabled={
                      loadingAction !== null ||
                      resetOtp.length !== 6 ||
                      newPassword.length < 6
                    }
                    startIcon={
                      loadingAction === 'VERIFYING_RESET' || resetOtp.length !== 6 ? (
                        <CircularProgress size={20} sx={{ color: '#ffffff' }} />
                      ) : null
                    }
                    sx={{
                      mt: 2,
                      mb: 1,
                      py: 1,
                      height: '36px',
                      backgroundColor: '#2e7d32',
                      '&:hover': { backgroundColor: '#1b5e20', transform: 'scale(1.02)' },
                      '&.Mui-disabled': { backgroundColor: '#a5d6a7', color: '#fff' },
                    }}>
                    {loadingAction === 'VERIFYING_RESET'
                      ? 'Verifying...'
                      : 'Set New Password'}
                  </Button>

                  <Button
                    fullWidth
                    variant='outlined'
                    disabled={loadingAction !== null}
                    onClick={() => {
                      setResetMode(false);
                      setResetOtp('');
                      setNewPassword('');
                    }}
                    sx={{
                      mb: 1,
                      height: '36px',
                      color: '#666',
                      borderColor: '#ccc',
                      '&:hover': { backgroundColor: '#f5f5f5', borderColor: '#999' },
                    }}>
                    Cancel
                  </Button>
                </Box>
              </>
            ) : (
              <>
                <Typography
                  component='h1'
                  variant='h5'
                  sx={{ mb: 1, fontWeight: 'bold' }}>
                  Sign In
                </Typography>

                <Box
                  component='form'
                  onSubmit={handleSubmit}
                  noValidate
                  sx={{ mt: 1, width: '100%' }}>
                  <TextField
                    size='small'
                    margin='dense'
                    required
                    fullWidth
                    id='email'
                    label='Email Address'
                    autoComplete='email'
                    autoFocus
                    disabled={loadingAction !== null}
                    value={userInfo.email}
                    onChange={(e) => setUserInfo({ ...userInfo, email: e.target.value })}
                    sx={inputStyle}
                  />

                  <TextField
                    size='small'
                    margin='dense'
                    required
                    fullWidth
                    name='password'
                    label='Password'
                    type={showPassword ? 'text' : 'password'}
                    id='password'
                    autoComplete='current-password'
                    disabled={loadingAction !== null}
                    value={userInfo.password}
                    onChange={(e) =>
                      setUserInfo({ ...userInfo, password: e.target.value })
                    }
                    sx={inputStyle}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position='end'>
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            size='small'>
                            {showPassword ? (
                              <VisibilityOff fontSize='small' />
                            ) : (
                              <Visibility fontSize='small' />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Button
                    type='submit'
                    fullWidth
                    variant='contained'
                    disabled={loadingAction !== null}
                    startIcon={
                      loadingAction === 'EMAIL' ? (
                        <CircularProgress size={20} sx={{ color: '#ffffff' }} />
                      ) : null
                    }
                    sx={{
                      mt: 2,
                      mb: 1,
                      py: 1,
                      height: '36px',
                      backgroundColor: '#1976d2',
                      '&:hover': { backgroundColor: '#1565c0', transform: 'scale(1.02)' },
                      '&:active': { backgroundColor: '#1976d2' },
                      '&.Mui-disabled': { backgroundColor: '#1976d2', color: '#fff' },
                    }}>
                    {loadingAction === 'EMAIL' ? 'Signing in...' : 'Sign In'}
                  </Button>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      width: '100%',
                      mb: 2,
                      gap: 1,
                    }}>
                    <Box>
                      {loadingAction === 'RESET' ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={16} sx={{ color: '#1976d2' }} />
                          <Typography
                            variant='body2'
                            sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                            Sending...
                          </Typography>
                        </Box>
                      ) : (
                        <Typography
                          onClick={handlePassword}
                          sx={{
                            color: '#1976d2',
                            fontSize: '0.875rem',
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            opacity: loadingAction !== null ? 0.5 : 1,
                            pointerEvents: loadingAction !== null ? 'none' : 'auto',
                          }}>
                          Forgot Password
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography
                        onClick={onSwitchToSignup}
                        sx={{
                          color: '#1976d2',
                          fontSize: '0.875rem',
                          cursor: 'pointer',
                          textDecoration: 'underline',
                          opacity: loadingAction !== null ? 0.5 : 1,
                          pointerEvents: loadingAction !== null ? 'none' : 'auto',
                        }}>
                        Sign up instead
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Button
                  onClick={handleGoogleButton}
                  fullWidth
                  variant='outlined'
                  disabled={loadingAction !== null}
                  startIcon={
                    loadingAction === 'GOOGLE' ? (
                      <CircularProgress size={20} sx={{ color: '#3c4043' }} />
                    ) : (
                      <img
                        src={GooogleImage}
                        alt='Google'
                        style={{ width: 24, height: 24, objectFit: 'contain' }}
                      />
                    )
                  }
                  sx={{
                    mb: 1,
                    backgroundColor: '#FFFFFF',
                    color: '#3c4043',
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '16px',
                    borderRadius: '4px',
                    borderColor: '#dadce0',
                    paddingY: 0,
                    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.5,
                    '&:hover': { backgroundColor: '#f7f8f8', borderColor: '#d2d2d2' },
                    '&:active': { backgroundColor: '#FFFFFF' },
                    '&.Mui-disabled': {
                      backgroundColor: '#FFFFFF',
                      borderColor: '#dadce0',
                      color: '#3c4043',
                    },
                  }}>
                  {loadingAction === 'GOOGLE' ? 'Connecting...' : 'Sign in with Google'}
                </Button>
              </>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </ThemeProvider>
  );
}
